import type { IVariable, IVariableType } from '@carboncopyco/types';
import { useCallback, useMemo } from 'react';
import {
  RulesContent,
  RulesManagerEmptySearch,
  RulesManagerFilters,
  useRulesManagerFilters,
} from '../RulesManager';
import { VariableForm } from '../VariableForm';
import { VariablesList, type VariablesListProps } from './VariablesList';

interface VariablesContentProps
  extends Pick<
    VariablesListProps,
    | 'variables'
    | 'editVariable'
    | 'deleteVariable'
    | 'reorderVariables'
    | 'onUseVariable'
  > {
  variableToCreate: IVariableType;
  createVariable: (variable: IVariable) => Promise<unknown>;
  cancelCreation: () => void;
}

export type Filters = 'all' | 'text' | 'image';

const ruleFilterToTypes: Record<Filters, IVariableType[]> = {
  all: ['text', 'image'],
  text: ['text'],
  image: ['image'],
};

export const VariablesContent = ({
  variableToCreate,
  variables,
  createVariable,
  editVariable,
  deleteVariable,
  reorderVariables,
  cancelCreation,
  onUseVariable,
}: VariablesContentProps) => {
  const filterByVariableType = useCallback(
    (variable: IVariable, filter: Filters) =>
      ruleFilterToTypes[filter ?? 'all'].includes(variable.type),
    []
  );

  const filterOptions = useMemo(
    () => ({
      initialFilter: 'all' as Filters,
      searchKeys: ['name'],
      dataSet: variables,
      filterFunc: filterByVariableType,
    }),
    [variables, filterByVariableType]
  );

  const {
    setSearchValue,
    searchValue,
    filter,
    setFilter,
    filteredList: filteredVariables,
    isFiltersActive,
  } = useRulesManagerFilters<Filters, IVariable>(filterOptions);

  return (
    <RulesContent>
      {!!variables?.length && (
        <RulesManagerFilters<Filters>
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onFiltersChange={setFilter}
          selectedFilter={filter}
          dropdownItems={[
            { id: 'all', label: 'Any type' },
            { id: 'image', label: 'Image' },
            { id: 'text', label: 'Text' },
          ]}
        />
      )}
      <RulesContent.CreationForm show={!!variableToCreate}>
        <VariableForm
          type={variableToCreate}
          onSubmit={createVariable}
          variables={variables}
          onCancel={cancelCreation}
          autoFocus
        />
      </RulesContent.CreationForm>
      {filteredVariables.length ? (
        <VariablesList
          isDraggingAllowed={!isFiltersActive}
          onVariableSelect={cancelCreation}
          variables={filteredVariables}
          editVariable={editVariable}
          deleteVariable={deleteVariable}
          reorderVariables={reorderVariables}
          onUseVariable={onUseVariable}
        />
      ) : !variableToCreate ? (
        <RulesManagerEmptySearch ruleType="variables" />
      ) : null}
    </RulesContent>
  );
};
